import { presentationGenerationApi } from "@/shared/services/presentation-generation/presentation-generation.api";
import type {
  EnqueuePresentationGenerationRequest,
  PresentationGenerationJobStatus,
  PresentationGenerationPartial,
  PresentationGenerationResult,
} from "@/shared/services/presentation-generation/types";
import axios from "axios";
import { useCallback, useRef, useState } from "react";
import { toast, type Id } from "react-toastify";
import {
  formatGenerationError,
  renderGenerationErrorToast,
} from "../utils/format-generation-error";
import {
  sendPdfToStorydocToast,
  updatePdfToStorydocToast,
} from "../utils/send-pdf-to-storydoc-toast";

type StartPresentationGenerationOptions = {
  cancelAriaLabel: string;
  errorMessage: string;
  loadingMessage: string;
  pollingMessage: string;
  request: EnqueuePresentationGenerationRequest;
  successMessage: string;
  onSuccess?: (result: PresentationGenerationResult) => void;
  suppressToast?: boolean;
  onStatus?: (msg: string) => void;
  onError?: (msg: string) => void;
  /**
   * Called with each run of slides the backend publishes while the job is
   * still running, in deck order. Lets a caller show the deck being built
   * instead of waiting for `onSuccess`. Omit it and nothing is streamed —
   * the poll then asks for no slides at all.
   */
  onSlides?: (slides: unknown[], meta: { total?: number }) => void;
  // Progressive slides: called each time the job publishes more drafts (one
  // call per fill wave), with everything published so far.
  onPartial?: (partial: PresentationGenerationPartial) => void;
  // Resume: poll an existing job instead of enqueuing a new one.
  existingJobId?: string;
  onEnqueued?: (jobId: string) => void;
};

type UsePresentationGenerationFlowOptions = {
  pollInterval?: number;
};

const DEFAULT_POLL_INTERVAL = 3000;

// Transient-failure tolerance for long-running generation jobs. The backend
// keeps the job in Redis even if a single status request fails (nodemon
// restart, brief network blip, proxy 502), so the polling loop should retry
// rather than abort and lose the in-flight work.
const TRANSIENT_RETRY_BACKOFFS_MS = [1000, 2000, 4000, 8000, 16000];

const isTransientNetworkError = (err: unknown): boolean => {
  if (!axios.isAxiosError(err)) return false;
  // No `response` means the request never reached the server (DNS failure,
  // connection reset, request abort by the browser, "Network Error").
  if (!err.response) return true;
  const status = err.response.status;
  return status === 502 || status === 503 || status === 504;
};

const sleepWithAbort = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });

export const withTransientRetry = async <T>(
  fn: () => Promise<T>,
  signal: AbortSignal,
): Promise<T> => {
  let lastErr: unknown;
  for (
    let attempt = 0;
    attempt <= TRANSIENT_RETRY_BACKOFFS_MS.length;
    attempt++
  ) {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    try {
      return await fn();
    } catch (err) {
      if (axios.isCancel(err) || (err as Error)?.name === "AbortError")
        throw err;
      if (!isTransientNetworkError(err)) throw err;
      lastErr = err;
      const backoff = TRANSIENT_RETRY_BACKOFFS_MS[attempt];
      if (backoff == null) break;
      await sleepWithAbort(backoff, signal);
    }
  }
  throw lastErr;
};

// Maps backend `currentStep` (snake_case from generator/index.js stepWithLog
// callsites) to a user-facing label. Steps not in the map fall back to the
// caller-supplied pollingMessage.
const STEP_LABELS: Record<string, string> = {
  pdf_fetch: "Fetching PDF…",
  pdf_validate: "Validating PDF…",
  files_api_upload: "Uploading PDF for analysis…",
  extract_images: "Extracting images…",
  classify_images: "Classifying images…",
  render_pages: "Rendering pages…",
  detect_regions: "Detecting regions…",
  crop_regions: "Cropping regions…",
  extract_charts: "Extracting chart data…",
  upload_images: "Uploading images…",
  deck_overview: "Planning deck narrative…",
  image_assignment: "Assigning images to slides…",
  slide_generation: "Generating slides…",
  postprocess: "Polishing slides…",
  prompt_generation: "Generating slides from prompt…",
};

export const usePresentationGenerationFlow = ({
  pollInterval = DEFAULT_POLL_INTERVAL,
}: UsePresentationGenerationFlowOptions = {}) => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const toastIdRef = useRef<Id | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const dismissProcessToast = useCallback(() => {
    if (!toastIdRef.current) return;

    toast.dismiss(toastIdRef.current);
    toastIdRef.current = null;
  }, []);

  const cancelActiveProcess = useCallback(() => {
    abortControllerRef.current?.abort();
    dismissProcessToast();
    setIsProcessing(false);
  }, [dismissProcessToast]);

  const updateProcessToast = useCallback((msg: string) => {
    if (!toastIdRef.current) return;

    // Use the shared renderer so the PDF icon and styling persist when the
    // step label changes; passing a plain string to `toast.update` would
    // replace the icon+text JSX with bare text.
    updatePdfToStorydocToast(toastIdRef.current, msg);
  }, []);

  const waitForNextPoll = useCallback(
    (signal: AbortSignal) => {
      return new Promise<void>((resolve, reject) => {
        const timer = window.setTimeout(resolve, pollInterval);

        signal.addEventListener(
          "abort",
          () => {
            window.clearTimeout(timer);
            reject(new DOMException("Aborted", "AbortError"));
          },
          { once: true },
        );
      });
    },
    [pollInterval],
  );

  const pollUntilDone = useCallback(
    async ({
      jobId,
      pollingMessage,
      signal,
      suppressToast,
      onStatus,
      onSlides,
      onPartial,
    }: {
      jobId: string;
      pollingMessage: string;
      signal: AbortSignal;
      suppressToast?: boolean;
      onStatus?: (msg: string) => void;
      onSlides?: (slides: unknown[], meta: { total?: number }) => void;
      onPartial?: (partial: PresentationGenerationPartial) => void;
    }): Promise<PresentationGenerationJobStatus> => {
      // How many streamed slides this poller has already handed on. Sent as
      // `since` so each poll carries only what is new — the backend publishes
      // slides as the fill lands, and a deck's worth of slide JSON on every
      // poll would be far heavier than the polling itself.
      let taken = 0;
      const drainSlides = (status: PresentationGenerationJobStatus) => {
        if (!onSlides) return;
        const fresh = status.slides;
        if (!Array.isArray(fresh) || fresh.length === 0) return;
        taken += fresh.length;
        onSlides(fresh, { total: status.slidesTotal });
      };

      let seenPartial = 0;
      while (!signal.aborted) {
        const status = await withTransientRetry(
          () =>
            presentationGenerationApi.getJobStatus(
              jobId,
              signal,
              onSlides ? taken : undefined,
            ),
          signal,
        );

        if (status.status === "done") {
          // Hand over anything published between the last poll and completion
          // before the caller moves on to the finished deck.
          drainSlides(status);
          return status;
        }

        if (status.status === "failed") {
          // Smuggle the structured error envelope through `cause` so the
          // catch site can render code/errorId/step in the failure toast.
          const failure = new Error(
            status.error?.message || "Storydoc generation failed.",
          );
          (failure as Error & { cause?: unknown }).cause = status.error;
          throw failure;
        }

        const completed = status.partial?.completed ?? 0;
        if (onPartial && completed > seenPartial) {
          seenPartial = completed;
          try {
            // no retry ladder here: a miss just waits for the next status poll
            onPartial(
              await presentationGenerationApi.getJobPartial(jobId, signal),
            );
          } catch {
            seenPartial = 0; // best-effort: refetch on the next poll; the final result still arrives through the normal path
          }
        }
        const stepLabel = status.currentStep
          ? STEP_LABELS[status.currentStep]
          : undefined;
        const label = stepLabel || pollingMessage;
        if (!suppressToast) updateProcessToast(label);
        onStatus?.(label);
        drainSlides(status);
        await waitForNextPoll(signal);
      }

      throw new DOMException("Aborted", "AbortError");
    },
    [updateProcessToast, waitForNextPoll],
  );

  const startGeneration = useCallback(
    async ({
      cancelAriaLabel,
      errorMessage,
      loadingMessage,
      pollingMessage,
      request,
      successMessage,
      onSuccess,
      suppressToast,
      onStatus,
      onError,
      onSlides,
      onPartial,
      existingJobId,
      onEnqueued,
    }: StartPresentationGenerationOptions) => {
      setError(null);
      setIsProcessing(true);

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (!suppressToast) {
        toastIdRef.current = sendPdfToStorydocToast({
          cancelAriaLabel,
          msg: loadingMessage,
          onCancel: cancelActiveProcess,
        });
      }
      onStatus?.(loadingMessage);

      let jobId: string | undefined;
      try {
        if (existingJobId) {
          jobId = existingJobId;
        } else {
          const enqueueResponse =
            await presentationGenerationApi.enqueueGeneration(
              request,
              controller.signal,
            );
          jobId = enqueueResponse.jobId;
        }
        onEnqueued?.(jobId);

        await pollUntilDone({
          jobId,
          pollingMessage,
          signal: controller.signal,
          suppressToast,
          onStatus,
          onSlides,
          onPartial,
        });
        const resultJobId = jobId;
        const finalStatus = await withTransientRetry(
          () =>
            presentationGenerationApi.getJobResult(
              resultJobId,
              controller.signal,
            ),
          controller.signal,
        );

        dismissProcessToast();

        if (finalStatus.result) {
          onSuccess?.(finalStatus.result);
        }

        if (!suppressToast) toast.success(successMessage);
      } catch (caught) {
        if (
          controller.signal.aborted ||
          (caught as Error).name === "AbortError"
        ) {
          return;
        }

        const formatted = formatGenerationError(caught, {
          jobId,
          fallbackMessage: errorMessage,
        });

        // Full payload to DevTools so the user can copy debug info into a chat.
        // eslint-disable-next-line no-console
        console.error("[presentation-generation] failure", formatted);

        setError(errorMessage);
        if (suppressToast) {
          onError?.(errorMessage);
        } else {
          dismissProcessToast();
          toast.error(renderGenerationErrorToast(formatted), {
            autoClose: false,
            style: { whiteSpace: "pre-line" },
          });
        }
      } finally {
        // a newer run may own the ref by now
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
          setIsProcessing(false);
        }
      }
    },
    [cancelActiveProcess, dismissProcessToast, pollUntilDone],
  );

  return {
    cancelActiveProcess,
    error,
    isProcessing,
    startGeneration,
  };
};
